/**
 * Flexible caching utility for TypeScript
 */
export function createCache<T>() {
  const cache = new Map<string, { 
    value: T, 
    timestamp: number 
  }>();

  return {
    get(key: string) {
      const entry = cache.get(key);
      return entry && (Date.now() < entry.timestamp) ? entry.value : undefined;
    },
    set(key: string, value: T, ttl: number) {
      cache.set(key, {
        value,
        timestamp: Date.now() + (ttl * 1000)
      });
    },
    delete(key: string) {
      cache.delete(key);
    },
    clear() {
      cache.clear();
    }
  };
}

// Global registry to track method caches and their keys
const methodCacheRegistry = new Map<string, Map<any, Map<string, any>>>();

/**
 * Decorator for caching method results
 * @param options Caching configuration
 * @returns Decorator function
 */
export function Cacheable(options: { 
  ttl?: number, 
  keyGenerator?: (...args: any[]) => string,
  keyGeneratorAsync?: (...args: any[]) => Promise<string>
} = {}) {
  const { 
    ttl = 3600, // Default 1 hour
    keyGenerator = (...args) => JSON.stringify(args), // Use all arguments for key generation
    keyGeneratorAsync = null
  } = options;

  // Create a cache store for method caches
  const methodCaches = new Map<any, Map<string, any>>();
  
  // Return a decorator function compatible with TypeScript experimental decorators
  return function(
    target: any, 
    propertyKey: string, 
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;
    
    // Register this method in the registry for cache invalidation
    const methodId = `${target.constructor.name}.${propertyKey}`;
    methodCacheRegistry.set(methodId, methodCaches);

    descriptor.value = async function(...args: any[]) {
      // Ensure a cache map exists for this method
      if (!methodCaches.has(this)) {
        methodCaches.set(this, new Map());
      }
      const methodCache = methodCaches.get(this)!;

      // Generate a unique cache key (support both sync and async key generation)
      let cacheKey: string;
      if (keyGeneratorAsync) {
        cacheKey = await keyGeneratorAsync(...args);
      } else {
        cacheKey = keyGenerator(...args);
      }
      
      // Check cached result
      const cachedEntry = methodCache.get(cacheKey);
      if (cachedEntry && (Date.now() < cachedEntry.timestamp)) {
        return cachedEntry.value;
      }

      // Call original method
      const result = await originalMethod.apply(this, args);

      // Cache the result if it's not null/undefined
      if (result !== null && result !== undefined) {
        methodCache.set(cacheKey, {
          value: result,
          timestamp: Date.now() + (ttl * 1000)
        });
      }

      return result;
    };

    return descriptor;
  };
}

/**
 * Invalidate a specific cache entry by its cache key pattern
 * @param className The class name containing the cached method
 * @param methodName The method name with the cache
 * @param keyPattern The pattern to match against cache keys (exact match or regex)
 */
export function invalidateCacheEntry(className: string, methodName: string, keyPattern: string | RegExp): void {
  const methodId = `${className}.${methodName}`;
  const methodCaches = methodCacheRegistry.get(methodId);
  
  if (!methodCaches) {
    console.warn(`No cache found for method: ${methodId}`);
    return;
  }

  // Iterate through all instances
  methodCaches.forEach((instanceCache) => {
    // If keyPattern is a string, do exact match
    if (typeof keyPattern === 'string') {
      instanceCache.delete(keyPattern);
    } 
    // If keyPattern is a RegExp, check each key
    else if (keyPattern instanceof RegExp) {
      const keysToDelete: string[] = [];
      
      instanceCache.forEach((_, key) => {
        if (keyPattern.test(key)) {
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => instanceCache.delete(key));
    }
  });
}

/**
 * Global cache management
 */
export const CacheManager = {
  // Store for named caches
  namedCaches: new Map<string, ReturnType<typeof createCache>>(),

  /**
   * Create a named cache instance
   * @param name Unique cache name
   * @returns Cache instance
   */
  createNamedCache<T>(name: string): ReturnType<typeof createCache<T>> {
    if (this.namedCaches.has(name)) {
      throw new Error(`Cache with name '${name}' already exists`);
    }

    const newCache = createCache<T>();
    this.namedCaches.set(name, newCache);
    return newCache;
  },

  /**
   * Get an existing named cache
   * @param name Cache name to retrieve
   * @returns Cache instance or undefined
   */
  getNamedCache<T>(name: string): ReturnType<typeof createCache<T>> | undefined {
    return this.namedCaches.get(name) as ReturnType<typeof createCache<T>>;
  },

  /**
   * Clear all caches or a specific named cache
   * @param name Optional cache name to clear
   */
  clear(name?: string) {
    if (name) {
      // Clear a specific named cache
      const cache = this.namedCaches.get(name);
      if (cache) {
        cache.clear();
        this.namedCaches.delete(name);
      }
    } else {
      // Clear all named caches
      this.namedCaches.forEach(cache => cache.clear());
      this.namedCaches.clear();
    }
  },

  /**
   * Invalidate a specific cache entry
   * @param className The class name containing the cached method
   * @param methodName The method name with the cache
   * @param keyPattern The pattern to match against cache keys (exact match or regex)
   */
  invalidateCacheEntry(className: string, methodName: string, keyPattern: string | RegExp): void {
    invalidateCacheEntry(className, methodName, keyPattern);
  }
};
